import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, LogOut } from 'lucide-react';
import { APP_IDS, type AppId, type AppParams, type User } from '@omnia/shared';
import { tr } from '../lib/i18n.ts';
import { useLogout } from '../lib/session.ts';
import { formatFullDate, formatWeekdayLong } from '../lib/format.ts';
import { Wallpaper } from '../wallpapers/Wallpaper.tsx';
import { SettingsWindow } from '../settings/SettingsWindow.tsx';
import { CompactInfoBar } from '../infobar/CompactInfoBar.tsx';
import { useInfoBarShortcut } from '../infobar/useInfoBarShortcut.ts';
import { APPS } from './registry.ts';
import { AppTile } from './AppTile.tsx';
import { AppBoundary, AppFallback } from './AppBoundary.tsx';
import { LaunchContext, WindowContext, type Launch } from './launch.ts';
import { sameParams, useDesktop } from './store.ts';
import { ConfirmDiscard } from './ConfirmDiscard.tsx';

/** Phones get a launcher and full-screen apps; floating windows make no sense at this size. */
export function MobileShell({ user }: { user: User }) {
  const [active, setActive] = useState<{ id: AppId; params: AppParams } | null>(null);
  const [subtitle, setSubtitle] = useState<string | null>(null);
  const [confirmingClose, setConfirmingClose] = useState(false);
  // Another app or file requested while the current one holds unsaved work.
  const [pendingLaunch, setPendingLaunch] = useState<{ id: AppId; params: AppParams } | null>(null);
  const activeRef = useRef(active);
  activeRef.current = active;
  const { setUnsaved } = useDesktop.getState();
  const isUnsaved = () => {
    const current = activeRef.current;
    return current ? Boolean(useDesktop.getState().unsaved[current.id]) : false;
  };
  const show = (next: { id: AppId; params: AppParams } | null) => {
    const current = activeRef.current;
    if (current) setUnsaved(current.id, false);
    setActive(next);
  };
  const launch = useMemo<Launch>(
    () => (id, params) => {
      const next = { id, params: params ?? {} };
      const current = activeRef.current;
      const same = current?.id === id && sameParams(current.params, next.params);
      if (!same && isUnsaved()) setPendingLaunch(next);
      else if (!same) show(next);
    },
    // Stable on purpose: the helpers read the latest state through refs and the store.
    [],
  );
  const windowContext = useMemo(
    () => ({
      params: active?.params ?? {},
      setParams: (params: AppParams) => setActive((current) => (current ? { ...current, params } : current)),
      setSubtitle,
      setUnsaved: (value: boolean) => {
        const current = activeRef.current;
        if (current) setUnsaved(current.id, value);
      },
      // The phone shell always shows one full-screen app.
      resize: () => {},
    }),
    [active?.params, setUnsaved],
  );
  useInfoBarShortcut();
  const logout = useLogout();
  const today = new Date();

  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      // Escape belongs to the innermost layer: open dialogs and text fields handle it themselves.
      const target = event.target as HTMLElement;
      if (
        document.querySelector('dialog[open]') ||
        target.closest('input, textarea, select, [contenteditable="true"], [role="menu"], [role="application"]')
      ) {
        return;
      }
      requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  function requestClose() {
    if (isUnsaved()) setConfirmingClose(true);
    else show(null);
  }

  if (active) {
    const app = APPS[active.id];
    const Body = app.component;
    return (
      <div className="mobile-app" style={{ ['--tone' as string]: `var(${app.tone})` }}>
        <header className="mobile-app-bar">
          <button type="button" className="mobile-back" onClick={requestClose}>
            <ChevronLeft size={20} aria-hidden />
            {tr.window.back}
          </button>
          <h1>
            {app.title}
            {subtitle && <span className="window-subtitle">{subtitle}</span>}
          </h1>
        </header>
        {confirmingClose && (
          <ConfirmDiscard
            onDiscard={() => {
              setConfirmingClose(false);
              show(null);
            }}
            onCancel={() => setConfirmingClose(false)}
          />
        )}
        {pendingLaunch && (
          <ConfirmDiscard
            variant="replace"
            onDiscard={() => {
              setPendingLaunch(null);
              show(pendingLaunch);
            }}
            onCancel={() => setPendingLaunch(null)}
          />
        )}
        <div className="window-body">
          <AppBoundary>
            <Suspense fallback={<AppFallback />}>
              <LaunchContext value={launch}>
                <WindowContext value={windowContext}>
                  <Body />
                </WindowContext>
              </LaunchContext>
            </Suspense>
          </AppBoundary>
        </div>
      </div>
    );
  }

  return (
    <LaunchContext value={launch}>
    <main className="launcher">
      <Wallpaper />
      <SettingsWindow />
      <CompactInfoBar />
      <header className="launcher-head">
        <img className="launcher-logo" src="/brand/omnia-logo.png" alt={tr.brandFull} width={142} height={50} draggable={false} />
        <p className="launcher-day">{formatWeekdayLong(today)}</p>
        <p className="launcher-date">{formatFullDate(today)}</p>
        {!user.guest && <p className="launcher-user">{tr.auth.welcome(user.name)}</p>}
      </header>
      <ul className="launcher-grid" aria-label={tr.dock.label}>
        {APP_IDS.map((id) => (
          <li key={id}>
            <button type="button" className="launcher-item" onClick={() => launch(id)}>
              <AppTile id={id} size={60} />
              <span>{APPS[id].title}</span>
            </button>
          </li>
        ))}
      </ul>
      {!user.guest && (
        <button type="button" className="btn launcher-logout" onClick={() => logout.mutate()} disabled={logout.isPending}>
          <LogOut size={16} aria-hidden />
          {tr.auth.logout}
        </button>
      )}
    </main>
    </LaunchContext>
  );
}
